import * as THREE from 'three';
import { mulberry32 } from '../core/rng';
import type { StrokeEvent } from '../state/strokes';
import { PaintSurface } from './PaintSurface';

/**
 * Consumes StrokeEvents (from the local player today; from a network layer
 * later) and turns them into GPU stamps on the right PaintSurface. The paint
 * textures are always derivable by replaying the stroke log through this
 * class — see ARCHITECTURE.md.
 */
export class PaintSystem {
  private surfaces = new Map<string, PaintSurface>();
  /** Meshes the spray raycaster may hit. */
  readonly paintableMeshes: THREE.Mesh[] = [];

  register(surface: PaintSurface): void {
    this.surfaces.set(surface.id, surface);
    surface.mesh.userData.surfaceId = surface.id;
    this.paintableMeshes.push(surface.mesh);
  }

  get(id: string): PaintSurface | undefined {
    return this.surfaces.get(id);
  }

  all(): Iterable<PaintSurface> {
    return this.surfaces.values();
  }

  /** Apply one stroke event: expand it into deterministic jittered micro-stamps. */
  applyStroke(stroke: StrokeEvent): void {
    const surface = this.surfaces.get(stroke.surfaceId);
    if (!surface) return;
    const color = new THREE.Color(stroke.color);
    const rand = mulberry32(stroke.seed);

    if (stroke.kind === 'footprint') {
      surface.enqueue({
        pos: new THREE.Vector3(...stroke.pos),
        color,
        radius: stroke.radius,
        alpha: stroke.opacity,
      });
      return;
    }

    // Airbrush grain: a cluster of small offset stamps instead of one flat disc.
    const subStamps = 4;
    for (let i = 0; i < subStamps; i++) {
      const jitter = stroke.radius * 0.3;
      const pos = new THREE.Vector3(
        stroke.pos[0] + (rand() * 2 - 1) * jitter,
        stroke.pos[1] + (rand() * 2 - 1) * jitter * 0.5,
        stroke.pos[2] + (rand() * 2 - 1) * jitter,
      );
      surface.enqueue({
        pos,
        color,
        radius: stroke.radius * (0.55 + rand() * 0.45),
        alpha: stroke.opacity * (0.45 + rand() * 0.4),
      });
    }
  }

  /** Rebuild every paint texture from the full stroke log (import, quality change). */
  replay(strokes: readonly StrokeEvent[], renderer: THREE.WebGLRenderer): void {
    for (const surface of this.surfaces.values()) surface.clear();
    for (const stroke of strokes) this.applyStroke(stroke);
    this.flush(renderer);
  }

  /** Push queued stamps to the GPU. Call once per frame before the main render. */
  flush(renderer: THREE.WebGLRenderer): void {
    for (const surface of this.surfaces.values()) surface.flush(renderer);
  }

  /** Snowfall gradually buries paint: whiten (fade) every surface. */
  fadeAll(renderer: THREE.WebGLRenderer, decay: number): void {
    for (const surface of this.surfaces.values()) surface.fade(renderer, decay);
  }

  /** Fresh snow everywhere. */
  clearAll(): void {
    for (const surface of this.surfaces.values()) surface.clear();
  }

  resizeAll(large: number, small: number): void {
    for (const surface of this.surfaces.values()) {
      surface.resize(surface.mesh.userData.largePaintSurface === true ? large : small);
    }
  }

  dispose(): void {
    for (const surface of this.surfaces.values()) surface.dispose();
    this.surfaces.clear();
    this.paintableMeshes.length = 0;
  }
}
